import * as React from 'react';
import './ChatChart.css';

/**
 * Renderer for the chart specs the assistant's create_chart tool streams,
 * ported from TITAN_dev's src/pages/Chat/ChatChart.tsx. Hand-drawn SVG rather
 * than a chart library, so there is no version skew to manage; colours come
 * from this app's validated palette instead of TITAN's.
 */
export interface ChartPoint { x: string; y: number }
export interface ChartSeries { name: string; points: ChartPoint[] }
export interface ChartSpec {
    chart_type: 'bar' | 'line' | 'donut' | 'table';
    title: string;
    x_label?: string;
    y_label?: string;
    series?: ChartSeries[];
    columns?: string[];
    rows?: (string | number | null)[][];
}

const SERIES_COLORS = ['var(--series-1)', 'var(--series-2)', 'var(--series-3)'];
const W = 460;
const H = 220;
const PAD = {top: 12, right: 12, bottom: 40, left: 44};
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;

const niceMax = (v: number): number => {
    if (v <= 0) return 1;
    const mag = 10 ** Math.floor(Math.log10(v));
    for (const m of [1, 2, 2.5, 5, 10]) if (v <= m * mag) return m * mag;
    return v;
};

const fmt = (v: number): string =>
    Math.abs(v) >= 1000 ? `${(v / 1000).toFixed(v >= 10000 ? 0 : 1)}k` : String(v);

function Table({spec}: {spec: ChartSpec}) {
    const columns = spec.columns ?? [];
    const rows = spec.rows ?? [];
    return (
        <div className="chat-chart__scroll">
            <table className="chat-chart__table">
                <thead>
                    <tr>{columns.map(c => <th key={c}>{c}</th>)}</tr>
                </thead>
                <tbody>
                    {rows.map((row, i) => (
                        <tr key={i}>{row.map((cell, j) => <td key={j}>{cell ?? '-'}</td>)}</tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}

const ChatChart: React.FC<{spec: ChartSpec}> = ({spec}) => {
    // A donut of shares reads as well as a table and worse than one at this
    // size, so it is tabulated rather than drawn.
    if (spec.chart_type === 'table' || spec.chart_type === 'donut' || !spec.series?.length) {
        return (
            <figure className="chat-chart">
                <figcaption className="chat-chart__title">{spec.title}</figcaption>
                <Table spec={spec} />
            </figure>
        );
    }

    const series = spec.series;
    const labels = series[0].points.map(p => p.x);
    const max = niceMax(Math.max(...series.flatMap(s => s.points.map(p => p.y)), 0));
    const xStep = PLOT_W / Math.max(labels.length, 1);
    const yOf = (v: number) => PAD.top + PLOT_H - (v / max) * PLOT_H;

    return (
        <figure className="chat-chart">
            <figcaption className="chat-chart__title">{spec.title}</figcaption>
            <svg viewBox={`0 0 ${W} ${H}`} className="chat-chart__svg" role="img" aria-label={spec.title}>
                {[0, 0.5, 1].map(t => (
                    <line
                        key={t}
                        x1={PAD.left} x2={W - PAD.right}
                        y1={yOf(max * t)} y2={yOf(max * t)}
                        stroke="var(--gridline)" strokeWidth={1}
                    />
                ))}
                {[0, 0.5, 1].map(t => (
                    <text key={t} x={PAD.left - 6} y={yOf(max * t) + 4}
                          textAnchor="end" className="chat-chart__tick">{fmt(max * t)}</text>
                ))}

                {spec.chart_type === 'bar'
                    ? series.map((s, si) => s.points.map((p, i) => {
                        const barW = Math.min(24, (xStep * 0.7) / series.length);
                        const x = PAD.left + i * xStep + xStep / 2 - (series.length * barW) / 2 + si * barW;
                        return (
                            <rect
                                key={`${s.name}-${p.x}`}
                                x={x + 1} width={Math.max(barW - 2, 1)}
                                y={yOf(p.y)} height={Math.max(PAD.top + PLOT_H - yOf(p.y), 0)}
                                fill={SERIES_COLORS[si % SERIES_COLORS.length]}
                            />
                        );
                    }))
                    : series.map((s, si) => (
                        <polyline
                            key={s.name}
                            fill="none"
                            stroke={SERIES_COLORS[si % SERIES_COLORS.length]}
                            strokeWidth={2}
                            strokeLinejoin="round"
                            strokeLinecap="round"
                            points={s.points.map((p, i) =>
                                `${PAD.left + i * xStep + xStep / 2},${yOf(p.y)}`).join(' ')}
                        />
                    ))}

                {labels.map((label, i) => (
                    <text key={label} x={PAD.left + i * xStep + xStep / 2} y={H - 14}
                          textAnchor="middle" className="chat-chart__tick">{label}</text>
                ))}
            </svg>

            {series.length > 1 && (
                <div className="chat-chart__legend">
                    {series.map((s, si) => (
                        <span key={s.name} className="chat-chart__legend-item">
                            <span className="chat-chart__swatch"
                                  style={{background: SERIES_COLORS[si % SERIES_COLORS.length]}} />
                            {s.name}
                        </span>
                    ))}
                </div>
            )}
        </figure>
    );
};

export default ChatChart;
